import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import {
  ClipboardList,
  Plus,
  Upload,
  MessageSquare,
  CheckCircle2,
  Clock,
  FileText,
  Calendar,
} from "lucide-react";

// Task creation dialog
export const CreateTaskDialog = ({ onCreateTask }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskData, setTaskData] = useState({
    title: "",
    description: "",
    task_type: "general",
    priority: "normal",
    due_date: "",
  });

  const handleSubmit = async () => {
    if (!taskData.title.trim()) {
      toast.error("Task title is required");
      return;
    }
    if (!taskData.description.trim()) {
      toast.error("Task description is required");
      return;
    }

    setLoading(true);
    const success = await onCreateTask(taskData);
    setLoading(false);

    if (success) {
      setOpen(false);
      setTaskData({
        title: "",
        description: "",
        task_type: "general",
        priority: "normal",
        due_date: "",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#2471A3] hover:bg-[#1A5276]" data-testid="create-task-btn">
          <Plus className="w-4 h-4 mr-2" />
          Assign Task
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign New Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Task Type *</label>
            <Select
              value={taskData.task_type}
              onValueChange={(value) => setTaskData({ ...taskData, task_type: value })}
            >
              <SelectTrigger data-testid="task-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    General Task
                  </div>
                </SelectItem>
                <SelectItem value="file_upload">
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    File Upload Request
                  </div>
                </SelectItem>
                <SelectItem value="information_request">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Information Request
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Title *</label>
            <Input
              value={taskData.title}
              onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
              placeholder="e.g., Upload Business License"
              data-testid="task-title-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Description *</label>
            <Textarea
              value={taskData.description}
              onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
              placeholder="Detailed instructions for the client..."
              rows={3}
              data-testid="task-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Priority</label>
              <Select
                value={taskData.priority}
                onValueChange={(value) => setTaskData({ ...taskData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Due Date</label>
              <Input
                type="date"
                value={taskData.due_date}
                onChange={(e) => setTaskData({ ...taskData, due_date: e.target.value })}
              />
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-[#2471A3] hover:bg-[#1A5276]"
            data-testid="submit-task-btn"
          >
            {loading ? "Creating..." : "Create Task"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Tasks list card
export const ClientTasksCard = ({ tasks = [], formatDate, onViewTask }) => {
  const getTaskTypeIcon = (type) => {
    switch (type) {
      case "file_upload":
        return <Upload className="w-4 h-4" />;
      case "information_request":
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <ClipboardList className="w-4 h-4" />;
    }
  };

  const getTaskTypeLabel = (type) => {
    switch (type) {
      case "file_upload":
        return "File Upload";
      case "information_request":
        return "Info Request";
      default:
        return "General";
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-amber-100 text-amber-800", icon: Clock },
      in_progress: { color: "bg-blue-100 text-blue-800", icon: Clock },
      completed: { color: "bg-green-100 text-green-800", icon: CheckCircle2 },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1 text-xs`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1).replace("_", " ")}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: "bg-red-100 text-red-800",
      normal: "bg-gray-100 text-gray-600",
      low: "bg-blue-100 text-blue-600",
    };
    return (
      <Badge className={`${priorityConfig[priority] || priorityConfig.normal} text-xs`}>
        {priority?.charAt(0).toUpperCase() + priority?.slice(1)}
      </Badge>
    );
  };

  const pendingTasks = tasks.filter((t) => t.status !== "completed");
  const completedTasks = tasks.filter((t) => t.status === "completed");

  return (
    <Card data-testid="client-tasks-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-[#2471A3]" />
          Tasks ({tasks.length})
        </CardTitle>
        <div className="flex gap-2 text-xs">
          <span className="text-amber-600">{pendingTasks.length} pending</span>
          <span className="text-green-600">{completedTasks.length} completed</span>
        </div>
      </CardHeader>
      <CardContent>
        {tasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No tasks assigned yet</p>
            <p className="text-sm mt-1">Use the "Assign Task" button to create one</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {tasks.map((task) => (
              <div
                key={task.task_id}
                className={`p-3 rounded-lg border ${
                  task.status === "completed" ? "bg-gray-50" : "bg-white"
                } hover:border-[#2471A3]/50 cursor-pointer transition-colors`}
                onClick={() => onViewTask && onViewTask(task)}
                data-testid={`task-item-${task.task_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div
                      className={`p-2 rounded-lg ${
                        task.task_type === "file_upload"
                          ? "bg-purple-100 text-purple-600"
                          : task.task_type === "information_request"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {getTaskTypeIcon(task.task_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[#1A2636] truncate">{task.title}</h4>
                      <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                        {task.description}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                        <span>{getTaskTypeLabel(task.task_type)}</span>
                        {task.due_date && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(task.due_date)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {getStatusBadge(task.status)}
                    {getPriorityBadge(task.priority)}
                  </div>
                </div>
                {/* Show response preview if completed with response */}
                {task.status === "completed" && task.response && (
                  <div className="mt-2 pt-2 border-t text-xs">
                    <p className="text-gray-500 truncate">
                      Response: {task.response.substring(0, 100)}...
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientTasksCard;
